import React, { useState, useEffect, useRef, useCallback } from "react";
import { Form, Button, Row, Col, FormGroup, FormLabel } from "react-bootstrap";
import { FileText, X } from "lucide-react";
import { apiUrl } from "../../config";
import { fetchDropdownByCategoryWise } from "../../service/DropdownService";
import { useToast } from "../toast/ToastProvider";
import RichTextEditor from "./RichTextEditor";

const initialState = {
  id: 0,
  category: "",
  location: "",
  title: "",
  description: "",
  imageUrl: "",
  address1: "",
  address2: "",
  address3: "",
  address4: "",
  phoneNo1: "",
  phoneNo2: "",
  phoneNo3: "",
  webUrl: "",
  type: "",
  pdfFiles: [],
  fbLink: "",
  wapLink: "",
  ytLink: "",
};
const initialLocation = {
  id: 0,
  // adId: 0,
  location: "",
  days: "",
  time: "",
};

const getAttachmentFileName = (url) => {
  if (!url) return "PDF";
  const parts = url.split("/");
  return parts[parts.length - 1] || "View PDF";
};

const getPdfFileFromItem = (item) => {
  if (item instanceof File) return item;
  return item?.file instanceof File ? item.file : null;
};

const isPdfFile = (file) => {
  if (file.type === "application/pdf") return true;
  return file.name?.toLowerCase().endsWith(".pdf");
};

const AttachmentCard = React.memo(function AttachmentCard({
  fileName,
  href,
  previewUrl,
  onRemove,
  isNew,
  readOnly,
}) {
  return (
    <div
      className="attachment-card border rounded-3 shadow-sm bg-white position-relative"
      style={{ width: "180px", minHeight: "160px" }}
    >
      {!readOnly && onRemove && (
        <button
          type="button"
          className="attachment-remove-btn"
          aria-label="Remove attachment"
          onClick={onRemove}
        >
          <X size={14} />
        </button>
      )}
      <div className="d-flex flex-column align-items-center justify-content-center h-100 p-2 text-center">
        {previewUrl ? (
          <div className="attachment-preview-frame w-100 mb-2">
            <iframe
              src={`${previewUrl}#toolbar=0`}
              title={`Preview ${fileName}`}
            />
          </div>
        ) : (
          <FileText size={32} color="#dc3545" strokeWidth={1.5} />
        )}
        <p
          className="small mb-1 mt-1 w-100 text-truncate"
          title={fileName}
          style={{ fontSize: "0.75rem" }}
        >
          {fileName}
        </p>
        {isNew && (
          <span
            className="badge bg-primary mb-1"
            style={{ fontSize: "0.65rem" }}
          >
            New
          </span>
        )}
        {href && (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="small"
            style={{ fontSize: "0.75rem" }}
          >
            View PDF
          </a>
        )}
      </div>
    </div>
  );
});

const AttachmentPreviewList = React.memo(function AttachmentPreviewList({
  legacyType,
  existingAttachments,
  newPdfFiles,
  readOnly,
  onRemoveNew,
  onRemoveExisting,
}) {
  const hasLegacyPdf =
    typeof legacyType === "string" &&
    legacyType &&
    legacyType.includes(".pdf");

  const hasAttachments =
    hasLegacyPdf ||
    existingAttachments.length > 0 ||
    (Array.isArray(newPdfFiles) && newPdfFiles.length > 0);

  return (
    <div className="d-flex flex-wrap gap-3">
      {hasLegacyPdf && (
        <AttachmentCard
          fileName={getAttachmentFileName(legacyType)}
          href={`${apiUrl}/${legacyType}`}
          previewUrl={`${apiUrl}/${legacyType}`}
          readOnly={readOnly}
        />
      )}

      {existingAttachments.map((attachment) => (
        <AttachmentCard
          key={`existing-${attachment.id}`}
          fileName={getAttachmentFileName(attachment.attachmentUrl)}
          href={`${apiUrl}/${attachment.attachmentUrl}`}
          previewUrl={`${apiUrl}/${attachment.attachmentUrl}`}
          readOnly={readOnly}
          onRemove={() => onRemoveExisting(attachment)}
        />
      ))}

      {Array.isArray(newPdfFiles) &&
        newPdfFiles.map((item) => {
          const file = getPdfFileFromItem(item);
          if (!file) return null;

          return (
            <AttachmentCard
              key={item.key || `new-${file.name}`}
              fileName={file.name}
              href={item.previewUrl}
              previewUrl={item.previewUrl}
              isNew
              readOnly={readOnly}
              onRemove={() => onRemoveNew(item.key)}
            />
          );
        })}

      {!hasAttachments && (
        <div className="text-muted small py-2">No attachments added yet.</div>
      )}
    </div>
  );
});

const AdvertisementForm = ({
  onSave,
  initialData,
  readOnly,
  loading,
  setLoading,
  setRemovedVisitingIds,
  setRemovedAttachmentIds = () => {},
}) => {
  const [advertisement, setAdvertisement] = useState(initialState);
  const [visitingTime, setVisitingTime] = useState([initialLocation]);
  const [previewImage, setPreviewImage] = useState(null);
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [categoryOption, setCategoryOption] = useState([]);
  const [locationOption, setLocationOption] = useState([]);
  const [communityCatergoryOption, setCommunityCatergoryOption] = useState([]);
  const [categoryType, setCategoryType] = useState("healthcare");
  const [linkType, setLinkType] = useState("url");
  const pdfFilesRef = useRef([]);

  const revokePdfPreviewUrls = (items = []) => {
    items.forEach((item) => {
      if (item?.previewUrl) {
        URL.revokeObjectURL(item.previewUrl);
      }
    });
  };

  useEffect(() => {
    if (initialData) {
      if (initialData.webUrl) {
        setLinkType("url");
      } else if (
        (initialData.attachments && initialData.attachments.length > 0) ||
        (initialData.pdfFiles &&
          (Array.isArray(initialData.pdfFiles)
            ? initialData.pdfFiles.length > 0
            : initialData.pdfFiles)) ||
        (typeof initialData.type === "string" &&
          initialData.type &&
          initialData.type.includes(".pdf"))
      ) {
        setLinkType("attachment");
      }
    }
  }, [initialData]);

  const toast = useToast();

  useEffect(() => {
    return () => {
      if (previewImage && previewImage.startsWith("blob")) {
        URL.revokeObjectURL(previewImage);
      }
    };
  }, [previewImage]);

  useEffect(() => {
    pdfFilesRef.current = advertisement.pdfFiles || [];
  }, [advertisement.pdfFiles]);

  useEffect(() => {
    return () => {
      revokePdfPreviewUrls(pdfFilesRef.current);
    };
  }, []);

  useEffect(() => {
    if (initialData) {
      setAdvertisement({
        ...initialState,
        ...initialData,
        fbLink: initialData.fbLink || "",
        wapLink: initialData.wapLink || "",
        ytLink: initialData.ytLink || "",
        pdfFiles: initialData.pdfFiles || [],
      });
      setExistingAttachments(
        Array.isArray(initialData.attachments) ? initialData.attachments : [],
      );
      if (initialData.imageUrl) {
        if (typeof initialData.imageUrl === "string") {
          setPreviewImage(apiUrl + "/" + initialData.imageUrl);
        } else if (initialData.imageUrl instanceof File) {
          setPreviewImage(URL.createObjectURL(initialData.imageUrl));
        }
      } else {
        setPreviewImage(null);
      }
      if (initialData?.visitingTime) {
        const vtArray = Array.isArray(initialData.visitingTime)
          ? initialData.visitingTime
          : [initialData.visitingTime];

        setVisitingTime(
          vtArray.map((vt) => ({
            id: vt.id ?? 0,
            location: vt.location || "",
            days: vt.days || "",
            time: vt.time || "",
          })),
        );
      }
    } else {
      setAdvertisement(initialState);
      setVisitingTime([initialLocation]);
      setPreviewImage(null);
      setExistingAttachments([]);
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAdvertisement((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAdvertisement((prev) => ({ ...prev, imageUrl: file }));
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  const handlePdfFilesChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const invalidFile = files.find(
      (file) => !isPdfFile(file) || file.size > 10 * 1024 * 1024,
    );
    if (invalidFile) {
      toast("Only PDF files up to 10 MB are allowed", "ERROR");
      e.target.value = "";
      return;
    }

    const newItems = files.map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
      key: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
    }));

    setAdvertisement((prev) => ({
      ...prev,
      pdfFiles: [...(prev.pdfFiles || []), ...newItems],
    }));
    e.target.value = "";
  };

  const removeNewPdfFile = useCallback((key) => {
    setAdvertisement((prev) => {
      const item = prev.pdfFiles.find((pdf) => pdf.key === key);
      if (item?.previewUrl) {
        URL.revokeObjectURL(item.previewUrl);
      }
      return {
        ...prev,
        pdfFiles: prev.pdfFiles.filter((pdf) => pdf.key !== key),
      };
    });
  }, []);

  const removeExistingAttachment = useCallback(
    (attachment) => {
      if (attachment.id) {
        setRemovedAttachmentIds((prev) => [...prev, attachment.id]);
      }
      setExistingAttachments((prev) =>
        prev.filter((item) => item.id !== attachment.id),
      );
    },
    [setRemovedAttachmentIds],
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    onSave(advertisement, visitingTime);
    setTimeout(() => {
      setLoading(false);
    }, 2000);
  };

  // Dropdown for location and category
  useEffect(() => {
    const fetchDropdown = async () => {
      try {
        const category = await fetchDropdownByCategoryWise(
          "HealthCare Category",
        );
        const communityCategory =
          await fetchDropdownByCategoryWise("Community Category");
        const location = await fetchDropdownByCategoryWise("Location");

        const sortedCategory = category.sort((a, b) =>
          a.title.localeCompare(b.title),
        );
        const sortedCommunity = communityCategory.sort((a, b) =>
          a.title.localeCompare(b.title),
        );
        const sortedLocation = location.sort((a, b) =>
          a.title.localeCompare(b.title),
        );

        setCategoryOption(sortedCategory);
        setLocationOption(sortedLocation);
        setCommunityCatergoryOption(sortedCommunity);

        // Detect correct category type for editing
        if (initialData?.category) {
          if (sortedCategory.some((c) => c.title === initialData.category)) {
            setCategoryType("healthcare");
          } else if (
            sortedCommunity.some((c) => c.title === initialData.category)
          ) {
            setCategoryType("community");
          }
        }
      } catch (error) {
        toast("Error in fetch category", "ERROR");
      }
    };
    fetchDropdown();
  }, [initialData]);

  const handleVisitingChange = (index, e) => {
    const { name, value } = e.target;
    const updated = [...visitingTime];
    updated[index][name] = value;
    setVisitingTime(updated);
  };
  const addVisitingTime = () => {
    setVisitingTime([
      ...visitingTime,
      { id: 0, location: "", days: "", time: "" },
    ]);
  };
  const removeVisitRow = (index) => {
    const list = [...visitingTime];
    const removed = list.splice(index, 1)[0];
    if (removed.id && removed.id !== 0) {
      setRemovedVisitingIds((prev) => [...prev, removed.id]);
    }
    setVisitingTime(list);
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Row className="mb-3">
        <Col md={12}>
          <Form.Group controlId="category">
            <Form.Label>
              Category Type <span style={{ color: "red" }}>*</span>
            </Form.Label>
            <div>
              <Form.Check
                inline
                label="HealthCare Category"
                type="radio"
                id="categoryType-health"
                value="healthcare"
                checked={categoryType === "healthcare"}
                onChange={() => {
                  setCategoryType("healthcare");
                  setAdvertisement((prev) => ({ ...prev, category: "" }));
                }}
                disabled={readOnly}
              />
              <Form.Check
                inline
                label="Community Category"
                type="radio"
                id="categoryType-community"
                value="community"
                checked={categoryType === "community"}
                onChange={() => {
                  setCategoryType("community");
                  setAdvertisement((prev) => ({ ...prev, category: "" }));
                }}
                disabled={readOnly}
              />
            </div>
          </Form.Group>
        </Col>
      </Row>
      <Row className="mb-3">
        <Col md={6}>
          <Form.Label>
            Select Category <span style={{ color: "red" }}>*</span>
          </Form.Label>
          <Form.Select
            name="category"
            value={advertisement.category}
            onChange={handleChange}
            disabled={readOnly}
            required
          >
            <option value="">Select Category</option>
            {(categoryType === "healthcare"
              ? categoryOption
              : communityCatergoryOption
            ).map((cat) => (
              <option key={cat.id} value={cat.title}>
                {cat.title}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={6}>
          <Form.Group controlId="location">
            <Form.Label>
              location <span style={{ color: "red" }}>*</span>
            </Form.Label>
            <Form.Select
              name="location"
              value={advertisement.location}
              onChange={handleChange}
              disabled={readOnly}
              required
            >
              <option value="">Select Location</option>
              {locationOption.map((loc) => (
                <option key={loc.id} value={loc.title}>
                  {loc.title}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>
      <Row className="mb-3">
        <Col md={12}>
          <Form.Group controlId="title">
            <Form.Label>
              Title <span style={{ color: "red" }}>*</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="title"
              value={advertisement.title}
              onChange={handleChange}
              disabled={readOnly}
              required
            />
          </Form.Group>
        </Col>
      </Row>
      <Row className="mb-3">
        <Col md={12}>
          <FormGroup>
            <FormLabel>
              Description <span style={{ color: "red" }}>*</span>
            </FormLabel>
            <RichTextEditor
              key={advertisement.id}
              initialValue={advertisement.description || ""}
              isEditable={!readOnly}
              onChange={(html) =>
                setAdvertisement((prev) => ({ ...prev, description: html }))
              }
            />
          </FormGroup>
        </Col>
      </Row>

      <Row className="mb-2">
        <Col md={6}>
          <Form.Group controlId="address1">
            <Form.Label>
              Address <span style={{ color: "red" }}>*</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="address1"
              value={advertisement.address1}
              onChange={handleChange}
              disabled={readOnly}
              required
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="phoneNo1">
            <Form.Label>
              PhoneNo <span style={{ color: "red" }}>*</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="phoneNo1"
              value={advertisement.phoneNo1}
              onChange={handleChange}
              disabled={readOnly}
              required
            />
          </Form.Group>
        </Col>
      </Row>
      <Row className="mb-2">
        <Col md={6}>
          <Form.Group controlId="address2">
            <Form.Control
              type="text"
              name="address2"
              value={advertisement.address2}
              onChange={handleChange}
              disabled={readOnly}
              required
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="phoneNo2">
            <Form.Control
              type="text"
              name="phoneNo2"
              value={advertisement.phoneNo2}
              onChange={handleChange}
              disabled={readOnly}
            />
          </Form.Group>
        </Col>
      </Row>
      <Row className="mb-2">
        <Col md={6}>
          <Form.Group controlId="address3">
            <Form.Control
              type="text"
              name="address3"
              value={advertisement.address3}
              onChange={handleChange}
              disabled={readOnly}
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="phoneNo3">
            <Form.Control
              type="text"
              name="phoneNo3"
              value={advertisement.phoneNo3}
              onChange={handleChange}
              disabled={readOnly}
            />
          </Form.Group>
        </Col>
      </Row>
      <Row className="mb-3">
        <Col md={6}>
          <Form.Group controlId="address4">
            <Form.Control
              type="text"
              name="address4"
              value={advertisement.address4}
              onChange={handleChange}
              disabled={readOnly}
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={12}>
          <Form.Label>Link Type</Form.Label>
          <div>
            <Form.Check
              inline
              label="URL"
              name="linkType"
              type="radio"
              id="radio-url"
              value="url"
              checked={linkType === "url"}
              onChange={() => {
                setLinkType("url");
                setAdvertisement((prev) => ({
                  ...prev,
                  type: "",
                  webUrl: "",
                  pdfFiles: [],
                }));
              }}
              disabled={readOnly}
            />
            <Form.Check
              inline
              label="Attachment"
              name="linkType"
              type="radio"
              id="radio-attachment"
              value="attachment"
              checked={linkType === "attachment"}
              onChange={() => {
                setLinkType("attachment");
                setAdvertisement((prev) => ({ ...prev, webUrl: "" }));
              }}
              disabled={readOnly}
            />
          </div>
        </Col>
      </Row>

      {linkType === "url" && (
        <Row className="mb-3">
          <Col md={12}>
            <Form.Group controlId="webUrl">
              <Form.Label>Web URL</Form.Label>
              <Form.Control
                type="text"
                name="webUrl"
                value={advertisement.webUrl || ""}
                onChange={handleChange}
                disabled={readOnly}
              />
            </Form.Group>
          </Col>
        </Row>
      )}

      {linkType === "attachment" && (
        <Row className="mb-3">
          <Col md={12}>
            <Form.Group controlId="pdfAttachment">
              <Form.Label>PDF Attachments</Form.Label>

              {!readOnly && (
                <div className="attachment-upload-box mb-3">
                 
                  <Form.Control
                    type="file"
                    accept="application/pdf"
                    multiple
                    onChange={handlePdfFilesChange}
                  />
                  <Form.Text
                    className="text-muted d-block mt-2"
                    style={{ fontSize: "0.75rem" }}
                  >
                    PDF only • Max 10 MB per file • Multiple files allowed
                  </Form.Text>
                </div>
              )}

              <AttachmentPreviewList
                legacyType={advertisement.type}
                existingAttachments={existingAttachments}
                newPdfFiles={advertisement.pdfFiles}
                readOnly={readOnly}
                onRemoveNew={removeNewPdfFile}
                onRemoveExisting={removeExistingAttachment}
              />
            </Form.Group>
          </Col>
        </Row>
      )}

      <Row className="mb-3">
        <Col md={4}>
          <Form.Group controlId="fbLink">
            <Form.Label>Facebook Link</Form.Label>
            <Form.Control
              type="text"
              name="fbLink"
              value={advertisement.fbLink || ""}
              onChange={handleChange}
              disabled={readOnly}
              placeholder="https://facebook.com/..."
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group controlId="wapLink">
            <Form.Label>LinkedIn Link</Form.Label>
            <Form.Control
              type="text"
              name="wapLink"
              value={advertisement.wapLink || ""}
              onChange={handleChange}
              disabled={readOnly}
              placeholder="https://linkedin.me/..."
            />
          </Form.Group>
        </Col>
        {/* <Col md={4}>
          <Form.Group controlId="wapLink">
            <Form.Label>WhatsApp Link</Form.Label>
            <Form.Control
              type="text"
              name="wapLink"
              value={advertisement.wapLink || ""}
              onChange={handleChange}
              disabled={readOnly}
              placeholder="https://wa.me/..."
            />
          </Form.Group>
        </Col> */}
        <Col md={4}>
          <Form.Group controlId="ytLink">
            <Form.Label>YouTube Link</Form.Label>
            <Form.Control
              type="text"
              name="ytLink"
              value={advertisement.ytLink || ""}
              onChange={handleChange}
              disabled={readOnly}
              placeholder="https://youtube.com/..."
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={12}>
          <Form.Group controlId="image">
            <Form.Label>Image</Form.Label>
            <Form.Control
              type="file"
              onChange={handleFileChange}
              disabled={readOnly}
            />
            {previewImage && (
              <div className="mt-2">
                <img
                  src={previewImage}
                  alt="Preview"
                  style={{
                    width: "100px",
                    height: "100px",
                    objectFit: "cover",
                    marginTop: "10px",
                  }}
                />
              </div>
            )}
          </Form.Group>
        </Col>
      </Row>
      {/*
      {visitingTime.map((vt, index) => (
        <Row className="mb-2" key={index}>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Location</Form.Label>
              <Form.Control
                name="location"
                value={vt.location}
                onChange={(e) => handleVisitingChange(index, e)}
                disabled={readOnly}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Days</Form.Label>
              <Form.Control
                name="days"
                value={vt.days}
                onChange={(e) => handleVisitingChange(index, e)}
                disabled={readOnly}
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Time</Form.Label>
              <Form.Control
                name="time"
                value={vt.time}
                onChange={(e) => handleVisitingChange(index, e)}
                disabled={readOnly}
              />
            </Form.Group>
          </Col>
        </Row>
      ))}
      {!readOnly && (
        <Button variant="primary" onClick={addVisitingTime} className="mb-3">
          Add Visiting Time
        </Button>
      )} */}

      {visitingTime.map((vt, index) => (
        <Row className="mb-2" key={index}>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Location</Form.Label>
              <Form.Control
                name="location"
                value={vt.location}
                onChange={(e) => handleVisitingChange(index, e)}
                disabled={readOnly}
              />
            </Form.Group>
          </Col>

          <Col md={4}>
            <Form.Group>
              <Form.Label>Days</Form.Label>
              <Form.Control
                name="days"
                value={vt.days}
                onChange={(e) => handleVisitingChange(index, e)}
                disabled={readOnly}
              />
            </Form.Group>
          </Col>

          <Col md={3}>
            <Form.Group>
              <Form.Label>Time</Form.Label>
              <Form.Control
                name="time"
                value={vt.time}
                onChange={(e) => handleVisitingChange(index, e)}
                disabled={readOnly}
              />
            </Form.Group>
          </Col>

          {/* DELETE BUTTON */}
          {!readOnly && (
            <Col md={1} className="d-flex align-items-end">
              {visitingTime.length > 1 && (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => removeVisitRow(index)}
                >
                  -
                </Button>
              )}
            </Col>
          )}
        </Row>
      ))}

      {/* ADD BUTTON */}
      {!readOnly && (
        <Button variant="primary" onClick={addVisitingTime} className="mb-3">
          Add Visiting Time
        </Button>
      )}

      {!readOnly && (
        <Row className="d-flex justify-content-end">
          <Col md={12}>
            <div className="form-buttons">
              <Button type="submit" className="me-2" disabled={loading}>
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Saving...
                  </>
                ) : (
                  "Save"
                )}
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  revokePdfPreviewUrls(advertisement.pdfFiles);
                  setAdvertisement(initialState);
                  setVisitingTime([initialLocation]);
                  setPreviewImage(null);
                  setExistingAttachments([]);
                }}
              >
                Clear
              </Button>
            </div>
          </Col>
        </Row>
      )}
    </Form>
  );
};

export default AdvertisementForm;
